import {
  Checkbox,
  Flex,
  FormLabel,
  Switch,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Tooltip,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { BorrowingBaseMechanicsForLoanProductGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import type {
  BorrowingBaseMechanicsForLoanProduct,
  EligibilityCriteria,
  CreditFacility,
} from '../../types';
import { createLoanProductOptions } from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Select } from 'packages/common-frontend/components/Select';

const CreditFacilityOption = ({
  creditFacilityId,
  creditFacilityName,
  selectedCreditFacilityId,
  setSelectedCreditFacilityId,
  currentEligibilityExclusionsMap,
  borrowingBaseMechanicsForLoanProducts,
}: {
  creditFacilityId: string;
  creditFacilityName: string;
  selectedCreditFacilityId: string;
  setSelectedCreditFacilityId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  currentEligibilityExclusionsMap: Map<string, Set<string>>;
  borrowingBaseMechanicsForLoanProducts: Omit<
    BorrowingBaseMechanicsForLoanProductGQLType,
    'creditFacilityId' | 'intermediateFields'
  >[];
}) => {
  let totalEligibilityTests = 0;
  let excludedEligibilityTests = 0;
  borrowingBaseMechanicsForLoanProducts.forEach((mechanics) => {
    totalEligibilityTests += mechanics.eligibilityCriteria.length;
    const excludedTestsSet =
      currentEligibilityExclusionsMap.get(
        `${creditFacilityId}+${mechanics.loanProductId}`,
      ) ?? new Set<string>();
    excludedEligibilityTests += excludedTestsSet.size;
  });
  return (
    <Flex
      mb="24px"
      padding="16px 24px"
      border="1px"
      borderColor={colors.gray[200]}
      borderRadius="8px"
      background={
        selectedCreditFacilityId === creditFacilityId
          ? colors.aurora[400]
          : 'white'
      }
      color={selectedCreditFacilityId === creditFacilityId ? 'white' : 'black'}
      onClick={() => setSelectedCreditFacilityId(creditFacilityId)}
      cursor="pointer"
      width="300px"
    >
      <Flex justifyContent="space-between" width="100%">
        <Text fontWeight="500">{creditFacilityName}</Text>
        <Text>{`(${excludedEligibilityTests}/${totalEligibilityTests})`}</Text>
      </Flex>
    </Flex>
  );
};

const EligibilityExclusionsTable = ({
  creditFacilityLoanProductKey,
  currentEligibilityExclusionsMap,
  setCurrentEligibilityExclusionsMap,
  eligibilityCriteria,
  readOnly,
}: {
  creditFacilityLoanProductKey: string;
  currentEligibilityExclusionsMap: Map<string, Set<string>>;
  setCurrentEligibilityExclusionsMap: React.Dispatch<
    React.SetStateAction<Map<string, Set<string>>>
  >;
  eligibilityCriteria: EligibilityCriteria[];
  readOnly: boolean;
}) => {
  const currentEligibilityExclusionSet =
    currentEligibilityExclusionsMap.get(creditFacilityLoanProductKey) ??
    new Set<string>();
  return (
    <Table>
      <Thead>
        <Tr>
          <Th width="10%">Exclude</Th>
          <Th width="90%">Eligibility Criteria</Th>
        </Tr>
      </Thead>
      <Tbody>
        {_.sortBy(eligibilityCriteria, ['eligibilityCriteriaKey']).map(
          (criteria) => {
            return (
              <Tr key={criteria.eligibilityCriteriaKey}>
                <Td textAlign="center">
                  <Checkbox
                    isDisabled={readOnly}
                    isChecked={currentEligibilityExclusionSet.has(
                      criteria.eligibilityCriteriaKey,
                    )}
                    onChange={(e) => {
                      setCurrentEligibilityExclusionsMap((originalMap) => {
                        const newMap = new Map<string, Set<string>>(
                          originalMap,
                        );
                        const eligibilityExclusionSet =
                          newMap.get(creditFacilityLoanProductKey) ??
                          new Set<string>();
                        if (e.target.checked) {
                          eligibilityExclusionSet.add(
                            criteria.eligibilityCriteriaKey,
                          );
                        } else {
                          eligibilityExclusionSet.delete(
                            criteria.eligibilityCriteriaKey,
                          );
                        }
                        newMap.set(
                          creditFacilityLoanProductKey,
                          eligibilityExclusionSet,
                        );
                        return newMap;
                      });
                    }}
                  />
                </Td>
                <Td>
                  <Tooltip
                    label={
                      <Text as="pre">{criteria.eligibilityConfigDetail}</Text>
                    }
                    maxWidth="min-content"
                    padding="16px"
                    hasArrow
                    borderRadius="4px"
                  >
                    <Text>
                      {commonUtils.toTitleCase(criteria.eligibilityCriteriaKey)}
                    </Text>
                  </Tooltip>
                </Td>
              </Tr>
            );
          },
        )}
      </Tbody>
    </Table>
  );
};

export const EligibilityExclusions = ({
  currentEligibilityExclusionsMap,
  setCurrentEligibilityExclusionsMap,
  creditFacilitiesForAssignmentGrouping,
  validLoanProductsForCreditFacilitiesMap,
  borrowingBaseMechanicsMap,
  readOnly,
}: {
  currentEligibilityExclusionsMap: Map<string, Set<string>>;
  setCurrentEligibilityExclusionsMap: React.Dispatch<
    React.SetStateAction<Map<string, Set<string>>>
  >;
  creditFacilitiesForAssignmentGrouping: CreditFacility[];
  validLoanProductsForCreditFacilitiesMap: Map<string, string[]>;
  borrowingBaseMechanicsMap: Map<string, BorrowingBaseMechanicsForLoanProduct>;
  readOnly: boolean;
}) => {
  const [selectedCreditFacilityId, setSelectedCreditFacilityId] = useState<
    string | undefined
  >(
    creditFacilitiesForAssignmentGrouping.length > 0
      ? creditFacilitiesForAssignmentGrouping[0].id
      : undefined,
  );

  const [selectedLoanProductId, setSelectedLoanProductId] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (selectedCreditFacilityId) {
      const validLoanProductIds =
        validLoanProductsForCreditFacilitiesMap.get(selectedCreditFacilityId) ??
        [];
      if (validLoanProductIds.length > 0) {
        setSelectedLoanProductId(validLoanProductIds[0]);
      }
    }
  }, [selectedCreditFacilityId, validLoanProductsForCreditFacilitiesMap]);

  const loanProductOptions = createLoanProductOptions({
    validLoanProductsForCreditFacilitiesMap,
    borrowingBaseMechanicsMap,
    creditFacilityId: selectedCreditFacilityId,
  });

  const creditFacilityLoanProductKey = `${selectedCreditFacilityId}+${selectedLoanProductId}`;

  const currentEligibilityCriteria = borrowingBaseMechanicsMap.get(
    creditFacilityLoanProductKey,
  )?.eligibilityCriteria;

  const areAllEligibilityCriteriaSelected = useCallback((): boolean => {
    const eligibilityExclusionSet =
      currentEligibilityExclusionsMap.get(creditFacilityLoanProductKey) ??
      new Set<string>();
    if (currentEligibilityCriteria) {
      return eligibilityExclusionSet.size === currentEligibilityCriteria.length;
    }
    return false;
  }, [
    currentEligibilityExclusionsMap,
    creditFacilityLoanProductKey,
    currentEligibilityCriteria,
  ]);

  const allEligibilityCriteriaSelected = areAllEligibilityCriteriaSelected();

  return (
    <>
      <Flex mt="24px" mb="24px" direction="column">
        <Text fontSize="md" mb="16px" fontWeight="600">
          Exclude Ineligible
        </Text>
        <Text fontSize="md" mb="16px" color="gray">
          Determine which eligibility criteria should be applied.
        </Text>
        <Flex
          padding="40px"
          border="1px"
          borderColor={colors.gray[200]}
          borderRadius="8px"
        >
          {selectedCreditFacilityId &&
          selectedLoanProductId &&
          currentEligibilityCriteria ? (
            <>
              <Flex direction="column">
                {creditFacilitiesForAssignmentGrouping.map((creditFacility) => {
                  return (
                    <CreditFacilityOption
                      key={creditFacility.id}
                      creditFacilityId={creditFacility.id}
                      creditFacilityName={creditFacility.name}
                      selectedCreditFacilityId={selectedCreditFacilityId}
                      setSelectedCreditFacilityId={setSelectedCreditFacilityId}
                      currentEligibilityExclusionsMap={
                        currentEligibilityExclusionsMap
                      }
                      borrowingBaseMechanicsForLoanProducts={
                        creditFacility.borrowingBaseMechanicsForLoanProducts
                      }
                    />
                  );
                })}
              </Flex>
              <Flex direction="column" ml="32px" width="100%">
                <Flex width="300px">
                  <Select
                    value={selectedLoanProductId}
                    options={loanProductOptions}
                    inputFloatingLabel="Loan Product"
                    inputFloatingLabelBackgroundColor={colors.white}
                    onChange={setSelectedLoanProductId}
                    width="300px"
                  />
                </Flex>
                <Flex alignItems="center" mt="24px" mb="16px">
                  <FormLabel
                    htmlFor="select-all-eligibility-criteria"
                    mb="0px"
                    fontWeight="400"
                    color={colors.black[70]}
                  >
                    Exclude all eligibility criteria
                  </FormLabel>
                  <Switch
                    bg="transparent"
                    isChecked={allEligibilityCriteriaSelected}
                    onChange={() => {
                      setCurrentEligibilityExclusionsMap((originalMap) => {
                        const newMap = new Map<string, Set<string>>(
                          originalMap,
                        );
                        let eligibilityExclusionSet =
                          newMap.get(creditFacilityLoanProductKey) ??
                          new Set<string>();
                        if (allEligibilityCriteriaSelected) {
                          eligibilityExclusionSet = new Set<string>();
                        } else {
                          currentEligibilityCriteria.forEach((criteria) => {
                            eligibilityExclusionSet.add(
                              criteria.eligibilityCriteriaKey,
                            );
                          });
                        }
                        newMap.set(
                          creditFacilityLoanProductKey,
                          eligibilityExclusionSet,
                        );
                        return newMap;
                      });
                    }}
                    id="select-all-eligibility-criteria"
                    isDisabled={readOnly}
                  />
                </Flex>
                <EligibilityExclusionsTable
                  creditFacilityLoanProductKey={creditFacilityLoanProductKey}
                  currentEligibilityExclusionsMap={
                    currentEligibilityExclusionsMap
                  }
                  setCurrentEligibilityExclusionsMap={
                    setCurrentEligibilityExclusionsMap
                  }
                  eligibilityCriteria={currentEligibilityCriteria}
                  readOnly={readOnly}
                />
              </Flex>
            </>
          ) : (
            <Text>
              A credit facility and/or loan product needs to be set up to use
              this feature.
            </Text>
          )}
        </Flex>
      </Flex>
      <hr />
    </>
  );
};
